<script setup lang="ts">
import { computed, ref } from 'vue';
import { useQuasar } from 'quasar';
import type { AxiosError } from 'axios';
import axios from 'axios';
import FlightSchoolRepository from '@/repositories/FlightSchoolRepository';
import type FlightSchool from '@/types/FlightSchool';
import type FlightSchoolCourse from '@/types/FlightSchoolCourse';
import type { FlightSchoolCourseStudent } from '@/types/FlightSchoolStudent';
import { useSchoolStore } from '@/stores/school';

const $q = useQuasar();

const props = defineProps<{
    school: FlightSchool;
    course: FlightSchoolCourse;
    student: FlightSchoolCourseStudent;
}>();

const lockedOut = ref<boolean>(props.student.lockout?.lockedOut ?? false);
const showDialog = ref<boolean>(false);

const lockLabel = computed(() => (lockedOut.value ? 'Unlock' : 'Lock'));
const lockButtonIcon = computed(() => (lockedOut.value ? 'sym_o_lock_open_right' : 'sym_o_lock'));
const lockButtonLabel = computed(() => `${lockLabel.value} Student`);
const lockDialogTitle = computed(() => `${lockLabel.value} Course Access?`);
const lockDialogButtonLabel = computed(() => `${lockLabel.value} Course`);
const lockDialogMessage = computed(() => (lockedOut.value
    ? 'By unlocking this student\'s course access they will be able access their material and continue their studies.'
    : 'By locking this student\'s course access they will no longer be able to make progress in their course.'
));
const emit = defineEmits(['updateLockout']);

const confirmLockUnlock = async () => {
    showDialog.value = false;

    $q.dialog({
        title: `Confirm ${lockLabel.value} Course Access`,
        message: `Are you sure you want to ${lockLabel.value.toLowerCase()} this student's course access?`,
        persistent: true,
        cancel: true,
    }).onOk(async () => {
        const action = lockedOut.value ? 'unlock' : 'lock';

        try {
            if (lockedOut.value === true) {
                await FlightSchoolRepository.unlockStudentCourseAccess(
                    props.school,
                    props.course,
                    props.student,
                ).catch(async (err: Error | AxiosError) => {
                    // Attempt to refresh the student upon unexpected 404
                    if (axios.isAxiosError(err) && err.response?.status === 404) {
                        const schoolStore = useSchoolStore();

                        await schoolStore.refreshStudent(props.student).then(() => {
                            const refreshedStudent = schoolStore.findStudent(props.student.id);

                            // Unlock shouldn't fail if the student is locked out, error.
                            if (refreshedStudent?.lockout?.lockedOut === true) {
                                throw err;
                            }
                        });
                    } else {
                        throw err;
                    }
                }).then(() => {
                    emit('updateLockout');
                });
            } else {
                await FlightSchoolRepository.lockStudentCourseAccess(
                    props.school,
                    props.course,
                    props.student,
                );
                emit('updateLockout');
            }

            $q.notify({
                type: 'positive',
                message: `Successfully ${action}ed student's course access`,
            });

            lockedOut.value = !lockedOut.value;
        } catch (error) {
            // todo: log error.
            $q.notify({
                type: 'negative',
                message: `Failed to ${action} student's course access`,
            });
        }
    });
};
</script>

<template>
    <q-btn
        id="course-lockout"
        class="anchor-button text-body2"
        color="fsum-med-blue"
        :label="lockButtonLabel"
        :icon="lockButtonIcon"
        :ripple="false"
        dense
        flat
        no-caps
        @click="() => showDialog = !showDialog"
    />
    <q-dialog
        v-model="showDialog"
        persistent
    >
        <q-card class="q-px-lg">
            <q-card-section class="row items-center q-pb-none">
                <q-space />
                <q-btn
                    v-close-popup="true"
                    flat
                    round
                    dense
                    icon="close"
                    aria-label="Close"
                />
            </q-card-section>
            <q-card-section class="row items-center q-px-xl p-py-none">
                <div class="text-h5 text-fsum-dark-blue q-pb-xs">
                    {{ lockDialogTitle }}
                </div>
                <p class="text-body1">
                    {{ lockDialogMessage }}
                </p>
            </q-card-section>
            <q-card-actions class="row justify-between q-px-xl q-pb-xl">
                <q-btn
                    outline
                    no-caps
                    class="fsum-button"
                    color="fsum-dark-blue"
                    label="Go Back"
                    icon="chevron_left"
                    @click="() => showDialog = false"
                />
                <q-btn
                    no-caps
                    class="fsum-button"
                    color="fsum-dark-blue"
                    text-color="white"
                    :label="lockDialogButtonLabel"
                    @click="confirmLockUnlock"
                />
            </q-card-actions>
        </q-card>
    </q-dialog>
</template>

<style scoped>
.fsum-button {
    padding: 0.625rem 0.75rem;
}
</style>
